<template>
  <v-card class="elevation-0">
    
    <v-select
      v-model="selectedResidentNames"
      :items="residentNames"
      :multiple="multiple"
      :disabled="disabled"
      :color="selectColor"
      readonly
      chips
      class="mb-3 mr-2"
      hide-details="auto"
      ref="input"
      >
      <template v-slot:label>
        <span :class="selectLabelClass">{{ label }}</span>
      </template>

      <template v-if="showTooltipButton" v-slot:prepend>
        <v-tooltip v-model="tooltip" top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small icon @click="tooltip = !tooltip">
              <v-icon>info</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-card-text class="py-3" v-html="tooltipText" />
          </v-card>
        </v-tooltip>
      </template>

      <template v-slot:append>
        <v-icon
          large
          @click="pickResidents"
          >group_add
        </v-icon>
      </template>

      <template v-slot:selection="{ item }">
        <v-chip
          dark
          close
          :color="chipColor"
          close-icon="mdi-close-outline"
          @click:close="removeResident(item)"
          >
          <span class="text-truncate">{{ item }}</span>
        </v-chip>
      </template>
    </v-select>
    <p v-if="error" class="red--text">{{ error }}</p>

    <v-dialog v-model="dialog" max-width="500px" :persistent="true">
      <loading
        :active.sync="isLoadingOrSaving"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />

      <template v-if="!isOptions">
        <v-card>
          <v-toolbar dark flat dense :color="toolbarColor" class="mb-3">
            <v-btn fab small @click="cancel" class="elevation-0" :color="toolbarColorDark">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-row justify="center">
              <h2 class="font-weight-light">
                {{ getExpression('Alerts', 'NoResidentsFound') }}
              </h2>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <template v-else>

        <!-- Header Toolbar -->
        <v-toolbar dark flat dense :color="toolbarColor">

          <!-- Cancel -->
          <v-btn fab small @click="cancel" class="elevation-0" :color="toolbarColorDark">
            <v-icon>close</v-icon>
          </v-btn>

          <v-toolbar-title><v-icon class="mx-3">people</v-icon>{{ this.getExpression('FormTitle', 'SelectResident') }}</v-toolbar-title>

          <v-spacer />
          
          <!-- OK -->
          <v-toolbar-items>
            <v-btn
              :disabled="selected.length === 0 || isSaving"
              :loading="isSaving"
              dark
              :color="toolbarColorDark"
              @click="select"
              class="elevation-0"
              >
              {{ getExpression('Button', 'OK') }}
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Data Table -->
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="filteredOptions"
          :loading="isLoading"
          :search="search"
          :custom-filter="customFilter"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="page"
          :footer-props="{'items-per-page-options': [10]}"
          show-select
          :multiple="false"
          item-key="id"
          >
          <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate />

          <template v-slot:top>
            <v-row no-gutters class="mx-3">

              <!-- Kiosk Type -->
              <template v-if="showKioskType">
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="kioskTypeId"
                    item-value="id"
                    item-text="description"
                    :items="kioskTypes"
                    :label="getExpression('Label', 'KioskType')"
                    :disabled="disableKioskType"
                    class="mr-3"
                    >
                    <template v-slot:selection="{ item }">
                      <v-chip small dark :color="getKioskTypeColor(item.id)">
                        <span>{{ item.description }}</span>
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>
              </template>

              <!-- Search -->
              <v-col cols="12" :sm="showKioskType ? '8' : '12'">
                <v-text-field
                  v-model="search"
                  append-icon="search"
                  :label="getExpression('Label', 'Search')"
                  single-line
                  hide-details
                  :disabled="isLoading"
                  clearable
                />
              </v-col>
            </v-row>
          </template>

          <template v-slot:item.picture="{ item }">
            <span class="text-center">
              <v-img width="40" height="40" :src="item.picture"></v-img>
            </span>
          </template>
        </v-data-table>
      </template>
    </v-dialog>
    
  </v-card>
</template>

<script>
import { AppColor, KioskTypeId, ImageUri } from '@/config.js'
import { mapGetters } from 'vuex'
import appService from '@/app.service.js'
import { EventBus } from '@/event-bus.js'

export default {
  name: 'ResidentSelector',
  $_veeValidate: {
    name () {
      return this.label
    },
    value () {
      return this.selectedResidentNames.length > 0
        ? this.selectedResidentNames
        : null
    }
  },
  emits: ['input'],
  props: {
    options: {
      type: Array,
      default: null
    },
    value: {
      type: Array,
      default: null
    },
    label: {
      type: String
    },
    multiple: {
      type: Boolean,
      default: true
    },
    showKioskColumn: {
      type: Boolean,
      default: false
    },
    showTooltipButton: {
      type: Boolean,
      default: false
    },
    tooltipText: {
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    },
    chipColor: {
      type: String,
      default: AppColor.Resident
    },
    error: {
      type: String,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {
      // colors
      appColor_Progress: AppColor.Progress,
      appColor_Genie: AppColor.KioskTypeGenie,
      appColor_Abby: AppColor.KioskTypeAbby,

      // kiosk type
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      // image uris
      imageUri_NoAvatar: ImageUri.NoAvatar,

      isLoading: false,
      isSaving: false,
      tooltip: false,

      page: 1,
      selected: [],
      kioskTypeId: KioskTypeId.Genie,
      search: '',
      sortBy: 'name',
      sortDesc: false, 

      dialog: false,
      pictures: null,
      selectedResidentIds: []
    }
  },
  computed: {
    ...mapGetters(['routeStack']),
    filteredOptions () {
      if (this.isNullOrUndefined(this.options)) return []
      const typeId = this.kioskTypeId
      const residents = this.options.filter(o => o.kioskTypeId === typeId)

      return residents
    },
    headers () {
      var headers = []
      headers.push({
        value: 'picture',
        align: 'center',
        width: '5%'
      })
      headers.push({
        text: this.getExpression('Label', 'Name'),
        value: 'name',
        align: 'left',
        width: this.showKioskColumn ? '50%' : '95%'
      })
      if (this.showKioskColumn) {
        headers.push({
          text: this.getExpression('Label', 'Kiosk'),
          value: 'kiosk',
          align: 'left',
          width: '45%'
        })   
      }
      return headers
    },
    residentNames () {
      return this.options !== null
        ? this.options.map(r => r.name)
        : []
    },
    selectedIds () {
      return this.value ?? []
    },
    selectedResidents () {
      const ids = this.selectedIds
      return this.options.filter(i => ids.includes(i.id))
    },
    selectedResidentNames () {
      return this.selectedResidents.map(r => r.name)
    },
    isLoadingOrSaving () {
      return this.isLoading || this.isSaving
    },
    kioskTypeIds () {
      return this.options
        .map(r => r.kioskTypeId)
        .filter(this.onlyUnique)
    },
    kioskTypes () {
      var kioskTypes = []

      if (this.kioskTypeIds.includes(this.kioskTypeId_Genie)) {
        kioskTypes.push({
          id: KioskTypeId.Genie,
          description: 'Genie'
        })
      }

      if (this.kioskTypeIds.includes(this.kioskTypeId_Abby)) {
        kioskTypes.push({
          id: KioskTypeId.Abby,
          description: 'Abby'
        })
      }

      return kioskTypes
    },
    isFromKioskDashboard () {
      return this.routeStack.includes('KioskDashboard')
    },
    isOptions () {
      return this.options.length > 0
    },
    showKioskType () {
      return !this.disableKioskType
    },
    disableKioskType () {
      return this.kioskTypeIds.length < 2
    },
    selectColor () {
      return this.error
        ? 'red'
        : ''
    },
    selectLabelClass () {
      return this.error
        ? 'red--text'
        : ''
    }
  },
  methods: {
    loadData () {      
      const self = this
      self.isLoading = true

      // check if the residents came with preloaded pictures
      const fetchPictures = self.options
        .filter(r => r.picture?.length === 0 || r.picture === null)
        .length > 0
      if (fetchPictures) self.loadPictures()

      if (self.kioskTypeIds.includes(self.kioskTypeId_Genie)) {
        self.kioskTypeId = this.kioskTypeId_Genie
      } else {
        self.kioskTypeId = this.kioskTypeId_Abby
      }

      self.isLoading = false
    },

    pickResidents () {
      const value = this.value
      this.selected = this.options.filter(o => value?.includes(o.id))
      this.loadData()
      this.dialog = true
    },

    select () {
      this.selectedResidentIds = this.selected.map(s => s.id)
      this.dialog = false
      this.emitValue()
    },

    cancel () {
      this.dialog = false
      this.reset()
    },

    focus () {
      this.$refs.input.focus()
    },

    reset () {
      const self = this
      setTimeout(() => {
        self.search = ''
        self.isLoading = false
        self.isSaving = false
        self.sortBy = 'name'
        self.sortDesc = false
        self.page = 1
        
      }, 300)
    },

    getKioskTypeColor (typeId) {
      if (typeId === this.kioskTypeId_Abby) {
        return this.appColor_Abby
      } else if (typeId === this.kioskTypeId_Genie) {
        return this.appColor_Genie
      } else return 'grey'
    },

    loadPictures () {
      const self = this
      const ids = !self.isAdmin
        ? self.options.map(r => r.id)
        : null
      
      if (ids !== null) {
        appService.getResidentPicturesByIds(ids).then((data) => {
          self.pictures = data.pictures
          self.populatePictures()
        })
      } else {
        appService.getResidentPictures().then((data) => {
        self.pictures = data.pictures
        self.populatePictures()
      })
      }
    },

    populatePictures () {
      const self = this
      self.options.forEach(o => {
        o.picture = self.getPicture(o.id)
      })
    },

    getPicture (id) {
      if (this.pictures === null) return ''
      const self = this
      var pictures = self.pictures.filter((value) => {
        return value.residentId === id
      })
      if (pictures.length > 0) {
        return pictures[0].base64
      }
      return this.imageUri_NoAvatar
    },

    customFilter (value, search, item) {
      return (
        (value != null &&
          search != null &&
          item.name
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1)
        )
    },
    
    removeResident (name) {
      const self = this

      // initialize these arrays when item is removed before any interaction with the datatable
      if (self.selected.length === 0) {
        self.selectedResidents.forEach(r => self.selected.push(r))
      }

      if (self.selectedResidentIds.length === 0) {
        self.selected.forEach(r => self.selectedResidentIds.push(r.id))
      }

      // standard remove logic
      var id = -1
      const idx1 = self.selected.findIndex(r => r.name === name)
      if (idx1 >= 0) {
        id = self.selected[idx1].id
        self.selected.splice(idx1, 1)
      }

      const idx2 = self.selectedResidentIds.findIndex(sid => sid === id)
      if (idx2 >= 0) {
        self.selectedResidentIds.splice(idx2, 1)
      }

      self.emitValue()
    },

    emitValue () {
      // emit the bound valud
      const value = this.selectedResidentIds.length > 0
        ? this.selectedResidentIds
        : null
      this.$emit('input', value)
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  beforeMount() {
    EventBus.$on('mouse-click', () => {
      if (this.tooltip === true) {
        this.tooltip = false
      }
    })
  },
  watch: {
    selectedResidentIds () {
      this.emitValue()
    }
  }
}
</script>
